// Bulletproof JSON fetch — uses XMLHttpRequest under the hood to side-step the
// Fetch API Body stream issues that have plagued this app in production.
// XHR.responseText is a plain string — no streams, no clone(), no
// "body stream already read" possible.
//
// Public API mirrors the previous fetch-based helper:
//   const { ok, status, data, text, error } = await safeJsonFetch(url, options);

const DEFAULT_TIMEOUT_MS = 60000;

export function safeJsonFetch(url, options = {}) {
  const {
    method = 'GET',
    headers = {},
    body = null,
    timeout = DEFAULT_TIMEOUT_MS,
  } = options;

  return new Promise((resolve) => {
    let settled = false;
    const settle = (result) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };

    let xhr;
    try {
      xhr = new XMLHttpRequest();
    } catch (e) {
      settle({ ok: false, status: 0, data: null, text: '', error: 'XHR unavailable' });
      return;
    }

    try {
      xhr.open(method, url, true);
    } catch (e) {
      settle({ ok: false, status: 0, data: null, text: '', error: `xhr.open failed: ${e.message || e}` });
      return;
    }

    if (timeout > 0) xhr.timeout = timeout;
    xhr.responseType = ''; // default — responseText will be a string

    try {
      Object.entries(headers).forEach(([k, v]) => {
        if (v == null) return;
        try { xhr.setRequestHeader(k, v); } catch { /* ignore unsupported headers */ }
      });
    } catch (e) {
      // continue without these headers
    }

    xhr.onload = () => {
      const text = xhr.responseText || '';
      let data = null;
      let parseError = null;
      if (text) {
        try { data = JSON.parse(text); } catch (e) { parseError = e.message || 'Non-JSON'; }
      }
      const okHttp = xhr.status >= 200 && xhr.status < 300;
      settle({
        ok: okHttp,
        status: xhr.status,
        data,
        text,
        error: okHttp ? (parseError || null) : (data?.detail || parseError || text.slice(0, 200) || `HTTP ${xhr.status}`),
      });
    };

    xhr.onerror = () => {
      settle({ ok: false, status: xhr.status || 0, data: null, text: '', error: 'Network error' });
    };

    xhr.ontimeout = () => {
      settle({ ok: false, status: 0, data: null, text: '', error: `Request timed out after ${timeout}ms` });
    };

    xhr.onabort = () => {
      settle({ ok: false, status: 0, data: null, text: '', error: 'Request aborted' });
    };

    try {
      xhr.send(body);
    } catch (e) {
      settle({ ok: false, status: 0, data: null, text: '', error: `xhr.send failed: ${e.message || e}` });
    }
  });
}

export function authHeaders(extra = {}) {
  const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

/** Same as authHeaders but omits Content-Type so the browser can pick the
 *  correct one when sending FormData (which needs a `multipart/boundary=`).
 *  Setting Content-Type: application/json on a FormData body corrupts the
 *  multipart payload — FastAPI then receives an empty form and errors. */
export function authHeadersForFormData(extra = {}) {
  const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

/** Render any FastAPI/Pydantic-style error payload as a single human string.
 *  FastAPI validation errors come back as `[{loc, msg, type}, ...]` which
 *  React stringifies as `[object Object]` if interpolated directly. */
export function formatApiError(res, fallback = 'unknown error') {
  if (!res) return fallback;
  const detail = res.data?.detail ?? res.data?.error ?? res.error;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d)))
      .join('; ');
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  if (res.text && res.text.length > 0 && res.text.length < 500) return res.text;
  return res.error || fallback;
}
